import {
  Stage,
  type HeadingFragment,
  type ResponsiveColumnValue,
} from '@seek/cmsu-cms-connect';
import type { Brand, Country, Locale } from '@seek/melways-sites';
import format from 'format-number';

const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });

const customCountryNames: Record<string, string> = {
  ph: 'the Philippines',
  hk: 'Hong Kong',
};

const normaliseCountryCode = (country: string) => country.toLocaleLowerCase();

const displayBrands: Record<string, string> = {
  seek: 'SEEK',
  jobstreet: 'Jobstreet',
  jobsdb: 'Jobsdb',
};

enum AwardsSectionEnum {
  'starawards',
  'sara',

  // Temporary for -test section - to be remove
  'starawards-test',
  'sara-test',
}
export type AwardsSection = keyof typeof AwardsSectionEnum;

export interface getTitleProps {
  brand: Brand;
  section?: string;
  title?: string;
  site?: string;
}

export const currencySymbolByCountry = (country: Country) => {
  const normalisedCountry = normaliseCountryCode(country);
  switch (normalisedCountry) {
    case 'my':
      return 'RM';
    case 'id':
      return 'RP';
    case 'th':
      return '฿';
    case 'ph':
      return '₱';
    default:
      return '$';
  }
};

export const getCountryName = (country: Country): string => {
  try {
    const normalisedCountry = normaliseCountryCode(country);
    if (customCountryNames[normalisedCountry]) {
      return customCountryNames[normalisedCountry];
    }
    return displayNames.of(normalisedCountry.toUpperCase()) || country;
  } catch {
    return country;
  }
};

export const formatCurrency = (amount: number, country: Country) =>
  format({ prefix: currencySymbolByCountry(country) })(amount);

export const formatNumber = (
  value: number | string,
  locale: Locale | undefined,
) => {
  const resolvedLocale = locale && locale !== 'en-ID' ? locale : 'en-AU';
  return new Intl.NumberFormat(resolvedLocale).format(Number(value));
};

// Insert an item between each item in an array
// eg., insertBetweenItems([1, 2, 3], {x}) => [1, {x}, 2, {x}, 3]
export const insertBetweenArrayItems = (arr: any[], x: any) =>
  arr.flatMap((e) => [e, x]).slice(0, -1);

export const isDraft = (stage: Stage) => stage === Stage.Draft;

export const resolveBrands = (brand: Brand = 'seek') => displayBrands[brand];

export const encodeSvg = (svg: string) =>
  `'data:image/svg+xml;utf8,${encodeURIComponent(svg)}'`;

export interface PreviewQueryProps {
  stage: Stage;
  fetchPolicy: 'no-cache' | 'network-only';
  previewHeaders: object;
}

const getAwardsSection = (section?: string): AwardsSection | undefined => {
  if (!section) {
    return undefined;
  }
  const resolvedSection = section?.toLocaleLowerCase();
  return Object.keys(AwardsSectionEnum).includes(resolvedSection)
    ? (resolvedSection as AwardsSection)
    : undefined;
};

const getTitle = ({
  brand = 'seek',
  section,
  title,
  site,
}: getTitleProps): string => {
  const awardsSection = titleHelper.getAwardsSection(section);

  return awardsSection
    ? titleHelper.getTitleForStarAwardsOrSara({ title, section: awardsSection })
    : titleHelper.getTitleBySiteAndBrand({ title, brand, site });
};

const getTitleBySiteAndBrand = ({
  brand = 'seek',
  title,
  site,
}: getTitleProps) =>
  site?.includes('employer')
    ? `${title} | SEEK Employer`
    : `${title} | ${resolveBrands(brand)}`;

const getTitleForStarAwardsOrSara = ({
  title,
  section,
}: {
  title?: string;
  section: AwardsSection;
}) => {
  const titleIncludesHomeOrEmpty =
    !title || title?.toLocaleLowerCase() === 'home';
  const resolvedTitle = titleIncludesHomeOrEmpty ? '' : ` - ${title}`;
  return section === 'starawards'
    ? `SEEK STAR Awards${resolvedTitle}`
    : `SEEK SARA Awards${resolvedTitle}`;
};

export const titleHelper = {
  getTitle,
  getAwardsSection,
  getTitleBySiteAndBrand,
  getTitleForStarAwardsOrSara,
};

const responsiveColumns = ['mobile', 'tablet', 'desktop', 'wide'];

export const getHighestValuesFrom = (
  responsiveColumnValue: Partial<ResponsiveColumnValue>,
) => {
  const values = Object.keys(responsiveColumnValue)
    .filter((key) => responsiveColumns.includes(key))
    .map((key) => {
      const value = responsiveColumnValue[
        key as keyof ResponsiveColumnValue
      ] as number;
      return !isNaN(value) ? value : 0;
    });
  return values.reduce((a, b) => (a > b ? a : b));
};

export const isLargeCustomHeading = (heading: Partial<HeadingFragment>) => {
  if (!heading.customHeadingSize) {
    return false;
  }
  switch (heading.customHeadingSize) {
    case 'standard':
    case 'large':
    case 'xlarge':
    case 'xxlarge':
    case 'xxxlarge':
      return true;
    default:
      return false;
  }
};
